import { type FixedObject, type FluidObject } from '@lendi-jam/atoms';
import { embedMarketingTrackingIntoURL } from '@lendi-jam/jam-link';

import {
  ExtractedObject,
  PrismicButtonOnclick,
  PrismicLink,
  PrismicObject,
  PrismicRichTextProps,
  PrismicSharpImage,
  TextFromRichText,
  UrlObj,
} from '#contentTypes/PrismicTypes';

import { getUrlByEnvironment } from './environmentFunctions';

export const getPrismicText = (arr?: PrismicRichTextProps[]): string =>
  arr?.reduce((total, item) => total + item.text, '') || '';

export const getPrismicLink = (arr?: PrismicRichTextProps[]): string =>
  getUrlByEnvironment(
    arr?.reduce(
      (total, item) =>
        total + item.spans.reduce((spanTotal, spanItem) => spanTotal + (spanItem.data ? spanItem.data.url : ''), ''),
      ''
    ) || ''
  );

export const removeDimensionParams = (src?: string) => src?.replace(/(\?|\&)([w|h])\=([^&]+)/g, '');

export const parseSrc = (src?: string, sharpObject?: FixedObject | FluidObject) => {
  const parsedSrc = removeDimensionParams(src);
  const fullSrc = `${parsedSrc}&w=2560`;
  const srcSet = `${parsedSrc}&w=360 360w, ${parsedSrc}&w=768 768w, ${parsedSrc}&w=1440 1440w, ${parsedSrc}&w=1920 1920w, ${parsedSrc}&w=2560 2560w`;

  if (!!sharpObject && !!src) {
    sharpObject.src = fullSrc;
    sharpObject.srcSet = srcSet;
  }

  return sharpObject;
};

export const parseSharpObject = (src?: string, sharpImage?: PrismicSharpImage) => {
  if (!!sharpImage && !!src) {
    if (sharpImage.hasOwnProperty('fluid')) {
      parseSrc(src, sharpImage?.fluid);
    }
    if (sharpImage.hasOwnProperty('fixed')) {
      parseSrc(src, sharpImage?.fixed);
    }
  }

  return sharpImage;
};

export const extractPrismicValues = (obj: PrismicObject): ExtractedObject => {
  const keys = Object.keys(obj);
  const res = keys.reduce((total: ExtractedObject, item: string) => {
    const objItem = obj[item];

    if (objItem) {
      if ('url' in objItem) {
        total[item] = !!objItem.url ? objItem.url : '';
      } else if (Array.isArray(objItem)) {
        const rtItem = objItem as unknown as PrismicRichTextProps[];
        if (rtItem.every((arrayObj: PrismicRichTextProps) => 'text' in arrayObj)) {
          const prismicText = getPrismicText(rtItem);
          total[item] = !!prismicText ? prismicText : '';
        }
      }
    } else {
      total[item] = '';
    }
    return total;
  }, {});
  return res;
};

export const extractButtonValues = (button: PrismicRichTextProps[]) => {
  const buttonContent = Array.isArray(button) && button.length ? button[0] : null;
  const hasButton = buttonContent && !!buttonContent.spans && !!buttonContent.text;
  const buttonObject = {
    text: getPrismicText(button),
    url: getPrismicLink(button),
  };

  return hasButton ? buttonObject : undefined;
};

export const fixRichTextLinks = (richText: PrismicRichTextProps[], urlObj: UrlObj) =>
  richText.map((item) => {
    const res = JSON.parse(JSON.stringify(item));
    if (res.type !== 'paragraph') return res;
    res.spans = item.spans.map((span) => {
      const { type, uid } = span.data || {};
      if (!type || !uid) return span;

      if (type === 'generic_template') {
        return {
          ...span,
          data: {
            link_type: 'Web',
            url: embedMarketingTrackingIntoURL(urlObj[uid].url, false),
          },
        };
      } else if (type === 'layout') {
        return {
          ...span,
          data: {
            link_type: 'Web',
            url: embedMarketingTrackingIntoURL(`/lp/${uid}`, false),
          },
        };
      } else {
        return {
          ...span,
          data: {
            link_type: 'Web',
            url: embedMarketingTrackingIntoURL(`/${uid}`, false),
          },
        };
      }
    });
    return res;
  });

export const parsePrismicLink = (urlObj: UrlObj, link: PrismicLink) => {
  if (link?.url) return link.url;
  if (link?._meta && urlObj) {
    const { type, uid } = link._meta;
    switch (type) {
      case 'generic_template':
        return urlObj[uid].url;
      case 'landing_page':
        return `/lp/${uid}`;
      default:
        return `/${uid}`;
    }
  }
  return '';
};

export const getButton = (textObj?: TextFromRichText, linkObj?: PrismicLink, onClick?: PrismicButtonOnclick) => {
  const text = textObj?.text;
  const url = linkObj?.url;
  return text && url ? { text, url, onClick: onClick ? onClick : undefined } : undefined;
};
